import { useRef, useEffect } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { oneDark } from '@codemirror/theme-one-dark';
import { quietlight } from '@uiw/codemirror-themes-all';
import { buildPythonEditorExtensions, DEFAULT_EDITOR_SETTINGS } from '../editor/codemirrorConfig';
import { useTheme } from '../context/ThemeContext';

/**
 * CodeMirror Python для блоков конспекта — та же тема/настройки, что у CodeEditor.
 */
export default function KonspektPythonCodeMirror({
  value = '',
  onChange,
  readOnly = false,
  height = 'min(40vh, 220px)',
  className = '',
}) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const { theme } = useTheme();
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!hostRef.current) return;
    const themeExt = theme === 'dark' ? oneDark : quietlight;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged && !readOnly && onChangeRef.current) {
        onChangeRef.current(update.state.doc.toString());
      }
    });

    const extensions = [
      ...buildPythonEditorExtensions({
        settings: DEFAULT_EDITOR_SETTINGS,
        themeExt,
        height,
        readOnly,
      }),
      // Read-only CM внутри ProseMirror иначе схлопывает ведущие пробелы (отступы Python).
      EditorView.theme({
        '.cm-content': { whiteSpace: 'pre-wrap' },
        '.cm-line': { whiteSpace: 'pre-wrap' },
      }),
      ...(!readOnly ? [updateListener] : []),
    ];

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: hostRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [readOnly, height, theme]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (value !== current) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value },
      });
    }
  }, [value]);

  return <div className={`code-editor-wrapper konspekt-cm-wrap ${className}`.trim()} ref={hostRef} />;
}
